/** Where the web app starts a Cispracs ID sign-in (server/src/routes/oidc.ts). */
export const OIDC_LOGIN_PATH = '/api/auth/oidc/login';

/** Where the web app signs out of Bunker and of the Cispracs ID session. It
 *  is a same-origin form POST: the server ends the local session and then
 *  continues the browser to the provider's end_session endpoint. */
export const OIDC_SIGN_OUT_PATH = '/api/auth/oidc/sign-out';

/** `&lang=` for the provider's own sign-in/out page (amendment 12 ui_locales):
 *  the language Bunker is shown in. Anything but ru/en is left out. */
function langQuery(lang: string | undefined): string {
  return lang === 'ru' || lang === 'en' ? `lang=${lang}` : '';
}

/** oidcLoginHref signs in and returns to the current page — an invite link
 *  (/invite/<code>) is applied after the sign-in. The server accepts only a
 *  same-origin path and re-checks it. */
export function oidcLoginHref(location: { pathname: string; search: string }, lang?: string): string {
  const l = langQuery(lang);
  return `${OIDC_LOGIN_PATH}?returnTo=${encodeURIComponent(location.pathname + location.search)}${l ? `&${l}` : ''}`;
}

/** submitOidcSignOut posts the sign-out form, navigating the whole page. */
export function submitOidcSignOut(doc: Document = document, lang?: string): void {
  const form = doc.createElement('form');
  const l = langQuery(lang);
  form.method = 'POST';
  form.action = l ? `${OIDC_SIGN_OUT_PATH}?${l}` : OIDC_SIGN_OUT_PATH;
  form.style.display = 'none';
  doc.body.appendChild(form);
  form.submit();
}

/** How long a sign-out waits for this browser's push subscription to end. */
export const SIGN_OUT_PUSH_WAIT_MS = 2_000;

/**
 * signOutHere ends this browser's web-push subscription, then posts the
 * sign-out form. Pushes carry message previews, so they must not outlive the
 * sign-in. The server deletes the subscription with the session as well, so a
 * failed or slow unsubscribe never holds the sign-out: after `waitMs` the form
 * is posted anyway.
 */
export async function signOutHere(
  forgetPush: () => Promise<void>,
  opts: { doc?: Document; lang?: string; waitMs?: number } = {},
): Promise<void> {
  let timer: ReturnType<typeof setTimeout> | undefined;
  try {
    await Promise.race([
      forgetPush(),
      new Promise<void>((resolve) => {
        timer = setTimeout(resolve, opts.waitMs ?? SIGN_OUT_PUSH_WAIT_MS);
      }),
    ]);
  } catch (err) {
    // Not fatal: the server-side deletion on sign-out covers it.
    console.warn('sign-out: could not end the push subscription of this browser', err);
  } finally {
    if (timer) clearTimeout(timer);
  }
  submitOidcSignOut(opts.doc ?? document, opts.lang);
}

/** gateSignIn picks the signed-out gate's main action: Cispracs ID whenever
 *  the server has it on (also inside the Desktop shell, whose tabs share the
 *  platform cookie), else the shell's platform tab, else the platform site. */
export function gateSignIn(oidcLogin: boolean, desktopShell: boolean): 'oidc' | 'desktopTab' | 'platform' {
  if (oidcLogin) return 'oidc';
  return desktopShell ? 'desktopTab' : 'platform';
}

/** gateHint says whether the gate shows its "sign in on the platform first"
 *  note. With Cispracs ID on, the button signs in right here, so the note
 *  would describe a step that no longer exists. */
export function gateHint(signIn: ReturnType<typeof gateSignIn>): boolean {
  return signIn !== 'oidc';
}
